import PropTypes from 'prop-types';
import { BsSearch } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import ActivityFieldsByChannel from './ActivityFieldsByChannel';
import { useNewActivityForm } from './useNewActivityForm';

/**
 * Standalone modal for creating an activity. Company-centric: a company is
 * always required, a deal is optional. Can be opened from anywhere.
 *
 * Props:
 *   - companyId / companyName: pre-fill company (e.g. from CrmCompanyPage)
 *   - dealId / dealTitle: pre-fill deal (e.g. from pipeline quick-action)
 *   - If neither is provided, the user picks a company first, then optionally a deal.
 */

const MODES = [
	{ value: 'log', label: 'Eintragen', desc: 'Erledigt protokollieren' },
	{ value: 'todo', label: 'To-Do', desc: 'Für später planen' },
	{ value: 'note', label: 'Notiz', desc: 'Freier Text' },
];

const NewActivityModal = ({
	isOpen,
	onClose,
	companyId: prefillCompanyId,
	companyName: prefillCompanyName,
	dealId: prefillDealId,
	dealTitle: prefillDealTitle,
}) => {
	const form = useNewActivityForm({ prefillCompanyId, prefillCompanyName, prefillDealId, prefillDealTitle, onClose });

	return (
		<Modal isOpen={isOpen} onClose={onClose} as="form" onSubmit={form.handleSubmit} size="m">
			<Panel.Header onClose={onClose} subtitle="Plane eine Aufgabe oder protokolliere eine erledigte Aktion">
				Neue Aktivität
			</Panel.Header>
			<Panel.Body className="space-y-4">
				<div className="flex gap-1 bg-bgBase-400 rounded-card p-1">
					{MODES.map((m) => (
						<button
							key={m.value}
							type="button"
							onClick={() => form.setMode(m.value)}
							className={`flex-1 text-xs px-3 py-2 rounded-control transition-colors ${
								form.mode === m.value
									? 'bg-white text-primary-700 font-semibold'
									: 'text-text hover:text-text'
							}`}
						>
							{m.label}
						</button>
					))}
				</div>

				{form.showCompanyPicker && (
					<div className="relative" ref={form.companyDropdownRef}>
						<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
							Unternehmen *
						</label>
						<div className="relative">
							<TextInput
								value={form.companyInput}
								onChange={form.handleCompanyInputChange}
								onFocus={() =>
									form.companyResults.length > 0 &&
									!form.selectedCompany &&
									form.setShowCompanyDropdown(true)
								}
								placeholder="Unternehmen suchen …"
								className={`pr-8 ${form.selectedCompany ? '!border-primary-300 !bg-primary-100/50' : ''}`}
								required
							/>
							{!form.selectedCompany && (
								<BsSearch className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-bgBase-800 pointer-events-none" />
							)}
							{form.selectedCompany && (
								<button
									type="button"
									onClick={form.handleClearCompany}
									className="absolute right-2 top-1/2 -translate-y-1/2 text-bgBase-800 hover:text-text text-sm"
								>
									&times;
								</button>
							)}
						</div>

						{form.showCompanyDropdown && !form.selectedCompany && (
							<div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-card shadow-elevated border border-bgBase-500 max-h-48 overflow-y-auto">
								{form.companySearchLoading && (
									<div className="px-3 py-2 text-xs text-bgBase-800">Suche …</div>
								)}
								{!form.companySearchLoading &&
									form.companyResults.length === 0 &&
									form.companyInput.trim().length >= 2 && (
										<div className="px-3 py-3 text-xs text-bgBase-800 text-center">
											Kein Unternehmen gefunden
										</div>
									)}
								{form.companyResults.map((c) => (
									<button
										key={c.id}
										type="button"
										onClick={() => form.handleSelectCompany(c)}
										className="w-full text-left px-3 py-2 hover:bg-bgBase-300 transition-colors border-b border-bgBase-400 last:border-b-0"
									>
										<p className="text-sm text-text truncate">{c.companyName}</p>
									</button>
								))}
							</div>
						)}
					</div>
				)}

				{prefillCompanyId && !prefillDealId && (
					<div>
						<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
							Unternehmen
						</label>
						<TextInput value={prefillCompanyName ?? ''} disabled />
					</div>
				)}

				<div className="relative" ref={form.dealDropdownRef}>
					<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">Deal</label>
					<div className="relative">
						<TextInput
							value={form.dealInput}
							onChange={form.handleDealInputChange}
							onFocus={() =>
								form.dealResults.length > 0 && !form.selectedDeal && form.setShowDealDropdown(true)
							}
							placeholder="Optional: Deal zuordnen …"
							disabled={Boolean(prefillDealId)}
							className={`pr-8 ${form.selectedDeal ? '!border-primary-300 !bg-primary-100/50' : ''}`}
						/>
						{!form.selectedDeal && !prefillDealId && (
							<BsSearch className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-bgBase-800 pointer-events-none" />
						)}
						{form.selectedDeal && !prefillDealId && (
							<button
								type="button"
								onClick={form.handleClearDeal}
								className="absolute right-2 top-1/2 -translate-y-1/2 text-bgBase-800 hover:text-text text-sm"
							>
								&times;
							</button>
						)}
					</div>
					{form.selectedDeal && (
						<p className="text-[11px] text-primary-600 mt-0.5">
							{form.selectedDeal.companyNameSnapshot ?? 'Deal ausgewählt'}
						</p>
					)}

					{form.showDealDropdown && !form.selectedDeal && (
						<div className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-card shadow-elevated border border-bgBase-500 max-h-48 overflow-y-auto">
							{form.dealSearchLoading && <div className="px-3 py-2 text-xs text-bgBase-800">Suche …</div>}
							{!form.dealSearchLoading &&
								form.dealResults.length === 0 &&
								form.dealInput.trim().length >= 2 && (
									<div className="px-3 py-3 text-xs text-bgBase-800 text-center">
										Kein Deal gefunden
									</div>
								)}
							{form.dealResults.map((d) => (
								<button
									key={d.id}
									type="button"
									onClick={() => form.handleSelectDeal(d)}
									className="w-full text-left px-3 py-2 hover:bg-bgBase-300 transition-colors border-b border-bgBase-400 last:border-b-0"
								>
									<p className="text-sm text-text truncate">{d.title}</p>
									<p className="text-[11px] text-bgBase-800 truncate">{d.companyNameSnapshot}</p>
								</button>
							))}
						</div>
					)}
				</div>

				<ActivityFieldsByChannel
					mode={form.mode}
					activeChannels={form.activeChannels}
					channelTypeId={form.channelTypeId}
					setChannelTypeId={form.setChannelTypeId}
					isCustomChannel={form.isCustomChannel}
					customLabel={form.customLabel}
					setCustomLabel={form.setCustomLabel}
					isEmailChannel={form.isEmailChannel}
					showTemplateOption={form.showTemplateOption}
					templateMode={form.templateMode}
					setTemplateMode={form.setTemplateMode}
					isTemplateMode={form.isTemplateMode}
					emailTemplates={form.emailTemplates}
					postMailingTemplates={form.postMailingTemplates}
					selectedEmailTemplateId={form.selectedEmailTemplateId}
					setSelectedEmailTemplateId={form.setSelectedEmailTemplateId}
					selectedPostMailingTemplateId={form.selectedPostMailingTemplateId}
					setSelectedPostMailingTemplateId={form.setSelectedPostMailingTemplateId}
					eligibleContacts={form.eligibleContacts}
					selectedContactId={form.selectedContactId}
					setSelectedContactId={form.setSelectedContactId}
					resolvedCompanyId={form.resolvedCompanyId}
					title={form.title}
					setTitle={form.setTitle}
					body={form.body}
					setBody={form.setBody}
					dueAt={form.dueAt}
					setDueAt={form.setDueAt}
				/>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button type="button" variant="outline" size="sm" onClick={onClose}>
					Abbrechen
				</Button>
				<Button
					type="submit"
					variant="solid"
					size="sm"
					loading={form.createActivity.isPending}
					disabled={
						!form.resolvedCompanyId ||
						(form.mode !== 'note' && !form.title.trim()) ||
						(form.mode === 'note' && !form.body.trim()) ||
						(form.isTemplateMode && (!form.selectedContactId || !form.dueAt))
					}
				>
					{form.mode === 'todo' ? 'To-Do anlegen' : form.mode === 'note' ? 'Notiz speichern' : 'Eintragen'}
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

NewActivityModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	companyId: PropTypes.string,
	companyName: PropTypes.string,
	dealId: PropTypes.string,
	dealTitle: PropTypes.string,
};

export default NewActivityModal;
